import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, firstValueFrom, take } from 'rxjs';
import {
  activitiesSectionKeys,
  AppSectionKey,
} from './section-visibility.config';
import { PermissionResource } from '../connectors/backend';
import { PermissionsService } from '../services/permissions.service';
import { SectionVisibilityService } from '../services/section-visibility.service';

async function waitForPermissionsLoaded(
  permissions: PermissionsService,
): Promise<void> {
  await firstValueFrom(
    toObservable(
      computed(() => permissions.myPermissionsQuery.isSuccess()),
    ).pipe(filter(Boolean), take(1)),
  );
}

export function sectionVisibilityGuard(section: AppSectionKey): CanActivateFn {
  return async () => {
    const sectionVisibility = inject(SectionVisibilityService);
    const permissions = inject(PermissionsService);
    const router = inject(Router);

    await waitForPermissionsLoaded(permissions);

    if (sectionVisibility.canAccessSection(section)) {
      return true;
    }
    return router.createUrlTree(sectionVisibility.getFirstAccessibleRoute());
  };
}

/** Access granted when the user can access at least one of the given sections. */
export function anySectionVisibilityGuard(
  sections: AppSectionKey[],
): CanActivateFn {
  return async () => {
    const sectionVisibility = inject(SectionVisibilityService);
    const permissions = inject(PermissionsService);
    const router = inject(Router);

    await waitForPermissionsLoaded(permissions);

    if (sections.some((s) => sectionVisibility.canAccessSection(s))) {
      return true;
    }
    return router.createUrlTree(sectionVisibility.getFirstAccessibleRoute());
  };
}

/** Grants access to the activities page when any activity subsection is allowed. */
export const activitiesSectionGuard = anySectionVisibilityGuard(
  activitiesSectionKeys,
);

/** Guard generico: accesso consentito se l'utente ha almeno un permesso su una delle resources indicate. */
export function resourceGuard(resources: PermissionResource[]): CanActivateFn {
  return async () => {
    const permissions = inject(PermissionsService);
    const sectionVisibility = inject(SectionVisibilityService);
    const router = inject(Router);

    await waitForPermissionsLoaded(permissions);

    if (resources.some((r) => permissions.hasAny(r))) {
      return true;
    }
    return router.createUrlTree(sectionVisibility.getFirstAccessibleRoute());
  };
}

/** Access granted only with List on the "all" scope for the given resource. */
export function listAllPermissionGuard(
  resource: PermissionResource,
): CanActivateFn {
  return async () => {
    const permissions = inject(PermissionsService);
    const router = inject(Router);

    await waitForPermissionsLoaded(permissions);

    if (permissions.canListAll(resource)) {
      return true;
    }
    return router.createUrlTree(['/', 'settings', 'account']);
  };
}

/** Accesso consentito solo con update su scope "all" per la resource indicata. */
export function updateAllPermissionGuard(
  resource: PermissionResource,
): CanActivateFn {
  return async () => {
    const permissions = inject(PermissionsService);
    const router = inject(Router);

    await waitForPermissionsLoaded(permissions);

    if (permissions.canUpdateAll(resource)) {
      return true;
    }
    return router.createUrlTree(['/', 'settings', 'account']);
  };
}
